// Remote live session over WebRTC: streams live microphone audio between two
// peers and syncs transport (play/stop/seek/tempo) via a data channel.
// Signaling goes through a tiny WebSocket relay (server/signal.js).
//
// Recording is local: each peer's record-arm and input settings stay on its own
// machine (never sent or overwritten), and a take only ever captures that
// peer's own devices. Recording the *partner's* microphone needs the partner's
// permission, asked for and granted per session — a grant ends when the
// connection does, and the partner can revoke it any time. (This is consent
// between WebCut clients; the live voice itself is streamed as in any call.)
import { normalizeProject, Track } from "../state/model";
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { wavBytes } from "../io/export-wav";

const ICE = [{ urls: "stun:stun.l.google.com:19302" }];

const bytesToB64 = (bytes: Uint8Array): string => {
  let s = ""; const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) s += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  return btoa(s);
};
const b64ToBytes = (b64: string): Uint8Array => {
  const s = atob(b64); const a = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i);
  return a;
};

/** Per-machine recording setup: never shared with (or taken from) the partner. */
type LocalTrackFields = Pick<Track, "armed" | "monitor" | "inputDeviceId" | "inputChannel" | "inputGain">;
const LOCAL_DEFAULTS: LocalTrackFields = { armed: false, monitor: false, inputDeviceId: null, inputChannel: undefined, inputGain: 0 };
const localFields = (t: Track): LocalTrackFields =>
  ({ armed: t.armed, monitor: t.monitor, inputDeviceId: t.inputDeviceId, inputChannel: t.inputChannel, inputGain: t.inputGain });

/** Compare shared content only (timestamps change on every edit). */
const sharedSig = (snap: any) => JSON.stringify({ ...snap, modifiedAt: 0 });

export class RemoteSession {
  private ws: WebSocket | null = null;
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private polite = false;
  private applyingRemote = false;
  private suppressBroadcast = false;
  private projTimer: any = null;
  private assetParts = new Map<string, { total: number; chunks: string[] }>();
  private storeUnsub: (() => void) | null = null;
  private localStream: MediaStream | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  // perfect-negotiation + presence state
  private iceServers: RTCIceServer[] = ICE;
  private makingOffer = false;
  private ignoreOffer = false;
  private peerPresent = false;
  private pendingIce: RTCIceCandidateInit[] = [];
  private wrapped = false;
  active = false;
  room: string | null = null;
  onStatus?: (s: string) => void;

  // ---- per-session recording permission ----
  /** May WE record the partner's mic this session? */
  remoteGrant: "none" | "pending" | "granted" | "denied" = "none";
  /** Did WE allow the partner to record our mic this session? */
  grantedToPeer = false;
  /** Ask the local user to allow/deny the partner's request (resolve true = allow). */
  onRecRequest?: () => Promise<boolean>;
  /** remoteGrant or grantedToPeer changed. */
  onRecPermission?: () => void;
  /** The partner started/stopped recording OUR mic. */
  onPeerRecording?: (on: boolean) => void;
  /** This machine's own audio round trip (output + input latency + calibrated
   *  offset, seconds) — sent to the partner so their recordings of us line up. */
  localLatency?: () => number;
  /** The partner's audio round trip as they reported it (seconds), if known. */
  partnerLatency: number | null = null;
  /** Partner's audio clock minus ours (seconds), from timestamp pings; null until measured. */
  clockOffset: number | null = null;
  private clockSamples: { rtt: number; off: number }[] = [];
  private pingTimer: any = null;
  /** Measured delay of the partner's audio path to our recorder (their outgoing
   *  audio → network → jitter buffer → our audio graph), seconds, and the jitter
   *  buffer in use when it was measured. Null until a probe succeeds. */
  pipelineDelay: number | null = null;
  pipelineJb: number | null = null;

  // ---- full-quality partner takes ----
  // While we record the partner's mic over the call, their app also records it
  // locally (exact timing, no codec) and sends that take when we stop; ours is
  // then replaced. Partner side: onPartnerTakeStart/Stop. Our side: onPartnerTake.
  onPartnerTakeStart?: (takeId: string) => void;
  onPartnerTakeStop?: (takeId: string) => void;
  /** A partner's take arrived: their audio, the audio-clock time (THEIR clock) of
   *  its first sample, and their device latency to subtract (seconds). */
  onPartnerTake?: (takeId: string, buf: AudioBuffer, startedAt: number, latency: number) => void;
  onPartnerTakeProgress?: (takeId: string, frac: number) => void;
  private takeParts = new Map<string, { total: number; chunks: string[]; at: number; latency: number }>();

  constructor(private store: Store, private engine: AudioEngine) {}

  private signalUrl() {
    const host = location.hostname;
    // Local dev: talk to `npm run signal` directly on 8787.
    if (host === "localhost" || host === "127.0.0.1") return "ws://localhost:8787";
    // Production: same-origin /signal path, reverse-proxied by nginx to the server.
    const proto = location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${location.host}/signal`;
  }

  async connect(room: string, isHost: boolean) {
    if (this.active) this.disconnect();          // leave any current session first
    this.polite = !isHost;                         // host = impolite peer
    this.active = true;
    this.room = room;
    this.peerPresent = false; this.makingOffer = false; this.ignoreOffer = false; this.pendingIce = [];
    this.suppressBroadcast = false; this.applyingRemote = false;
    this.resetRecPermissions();
    this.onStatus?.("connecting…");
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true } });
    } catch { this.onStatus?.("mic blocked"); }

    const ws = new WebSocket(this.signalUrl());
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: "join", room }));
    ws.onerror = () => this.onStatus?.("signal error");
    ws.onclose = () => { if (this.active) this.onStatus?.("offline"); };
    ws.onmessage = (ev) => this.onSignal(JSON.parse(ev.data));

    // The peer connection is created once the server sends our ICE config.
    // Install the transport + store hooks once; they no-op unless a session is active.
    if (!this.wrapped) {
      const prev = this.engine.onTransport;
      this.engine.onTransport = () => { prev?.(); if (this.active) this.broadcastTransport(); };
      this.storeUnsub = this.store.on("change", ({ scope }) => {
        if (this.active && (scope === "tracks" || scope === "mixer" || scope === "project")) this.broadcastProject();
      });
      this.wrapped = true;
    }
  }

  private setupPeer() {
    if (this.pc) return;
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    this.pc = pc;
    if (this.localStream) for (const t of this.localStream.getTracks()) pc.addTrack(t, this.localStream);

    pc.onicecandidate = (e) => { if (e.candidate) this.send({ type: "ice", candidate: e.candidate }); };
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState;
      if (st === "connected") this.onStatus?.("connected");
      else if (st === "failed") { this.onStatus?.("connection failed"); try { pc.restartIce(); } catch {} }
      else if (st === "disconnected") this.onStatus?.("disconnected");
    };
    pc.ontrack = (e) => {
      if (!this.remoteAudio) { this.remoteAudio = new Audio(); this.remoteAudio.autoplay = true; }
      this.remoteAudio.srcObject = e.streams[0];
      this.remoteAudio.play?.().catch(() => {});
    };
    // Negotiation is gated on peer presence so we never offer into an empty room.
    pc.onnegotiationneeded = () => { if (this.peerPresent && !this.polite) this.makeOffer(); };
    pc.ondatachannel = (e) => this.bindChannel(e.channel);
    // The data channel is created by the initiator in onPeers (role is server-assigned).
  }

  /** Tear down the peer connection but stay in the room (used when the other peer
   *  leaves, so a rejoin negotiates a fresh connection). */
  private resetPeer() {
    try { this.dc?.close(); } catch {}
    try { this.pc?.close(); } catch {}
    this.dc = null; this.pc = null;
    this.peerPresent = false; this.makingOffer = false; this.ignoreOffer = false;
    this.suppressBroadcast = false; this.applyingRemote = false;
    this.pendingIce = []; this.assetParts.clear();
    if (this.remoteAudio) { try { this.remoteAudio.srcObject = null; } catch {} }
    this.resetRecPermissions(); // a reconnect is a new session: ask again
    this.stopClockSync();
  }

  private async makeOffer() {
    const pc = this.pc;
    if (!pc) return;
    try {
      this.makingOffer = true;
      await pc.setLocalDescription();
      this.send({ type: "desc", desc: pc.localDescription });
    } catch (e) { console.error(e); }
    finally { this.makingOffer = false; }
  }

  private bindChannel(dc: RTCDataChannel) {
    this.dc = dc;
    dc.onopen = () => {
      this.onStatus?.("connected");
      // Only the initiator (host) pushes the project on connect; the joiner adopts
      // it. Otherwise the joiner's empty snapshot would clobber the host's tracks.
      if (!this.polite) this.broadcastProject(true);
    };
    dc.onmessage = (e) => this.onControl(JSON.parse(e.data));
    dc.onclose = () => { if (this.dc === dc) { this.resetRecPermissions(); this.stopClockSync(); } };
    if (dc.readyState === "open") this.startClockSync();
    dc.addEventListener("open", () => this.startClockSync());
  }

  private send(msg: any) { this.ws?.readyState === 1 && this.ws.send(JSON.stringify(msg)); }

  private onSignal(msg: any) {
    switch (msg.type) {
      case "config": this.iceServers = msg.iceServers?.length ? msg.iceServers : this.iceServers; this.setupPeer(); break;
      case "peers": this.onPeers(msg.count, msg.initiator); break;
      case "desc": this.onDesc(msg.desc); break;
      case "ice": this.onRemoteIce(msg.candidate); break;
      case "full": this.onStatus?.("room full (2 peers max)"); break;
    }
  }

  private onPeers(count: number, initiator?: boolean) {
    if (count >= 2) {
      // Server assigns roles by join order: the initiator offers, the other answers.
      if (typeof initiator === "boolean") this.polite = !initiator;
      if (!this.peerPresent) {
        this.peerPresent = true;
        this.setupPeer();
        if (!this.polite) { // initiator owns the data channel + first offer
          if (this.pc && !this.dc) this.bindChannel(this.pc.createDataChannel("ctrl"));
          this.makeOffer();
        }
        this.onStatus?.("connecting…");
      }
    } else {
      // Other peer left — drop the dead connection and wait for a rejoin.
      if (this.peerPresent || this.pc) { this.resetPeer(); this.onStatus?.("waiting for peer…"); }
    }
  }

  private async onDesc(desc: RTCSessionDescriptionInit) {
    this.setupPeer();
    const pc = this.pc!;
    this.peerPresent = true;
    try {
      const ready = !this.makingOffer && pc.signalingState === "stable";
      const offerCollision = desc.type === "offer" && !ready;
      this.ignoreOffer = !this.polite && offerCollision;
      if (this.ignoreOffer) return; // impolite peer keeps its own offer
      await pc.setRemoteDescription(desc); // polite peer implicitly rolls back on collision
      await this.flushIce();
      if (desc.type === "offer") {
        await pc.setLocalDescription();
        this.send({ type: "desc", desc: pc.localDescription });
      }
    } catch (e) { console.error("desc", e); }
  }

  private async onRemoteIce(candidate: RTCIceCandidateInit) {
    const pc = this.pc;
    if (!pc || !candidate) return;
    if (!pc.remoteDescription) { this.pendingIce.push(candidate); return; }
    try { await pc.addIceCandidate(candidate); } catch (e) { if (!this.ignoreOffer) console.warn("ice", e); }
  }

  private async flushIce() {
    const pc = this.pc;
    if (!pc?.remoteDescription) return;
    const cands = this.pendingIce; this.pendingIce = [];
    for (const c of cands) { try { await pc.addIceCandidate(c); } catch (e) { console.warn("ice", e); } }
  }

  private send2(msg: any) { if (this.dc?.readyState === "open") this.dc.send(JSON.stringify(msg)); }

  // ---- transport sync --------------------------------------------------
  private broadcastTransport() {
    if (this.applyingRemote) return;
    // While playing, send the exact anchor — the audio-clock time our timeline is
    // at `pos` — so the partner can start in step instead of "whenever it arrives".
    const a = this.engine.anchor();
    this.send2(a
      ? { t: "transport", playing: true, pos: a.pos, at: a.at, sent: this.engine.ctx.currentTime, tempo: this.store.project.tempo }
      : { t: "transport", playing: false, pos: this.engine.position(), tempo: this.store.project.tempo });
  }

  // ---- project state sync ---------------------------------------------
  private broadcastProject(immediate = false) {
    if (this.suppressBroadcast || this.dc?.readyState !== "open") return;
    clearTimeout(this.projTimer);
    const run = () => {
      const snap = this.sharedSnapshot();
      // Nothing the partner sees changed (e.g. only arm/input settings, which are
      // local) → don't send: a redundant snapshot could overwrite a fresh edit of theirs.
      const sig = sharedSig(snap);
      if (!immediate && sig === this.lastSharedSig) return;
      this.lastSharedSig = sig;
      this.send2({ t: "project", snapshot: snap });
    };
    if (immediate) run(); else this.projTimer = setTimeout(run, 180);
  }
  private lastSharedSig = "";
  /** The project as the partner may see it: recording setup reset to defaults. */
  private sharedSnapshot() {
    const snap = JSON.parse(JSON.stringify(this.store.project));
    for (const t of snap.tracks ?? []) Object.assign(t, LOCAL_DEFAULTS); // recording setup stays on this machine
    return snap;
  }

  private async drain() {
    const dc = this.dc;
    while (dc && dc.bufferedAmount > (1 << 20)) await new Promise((r) => setTimeout(r, 20));
  }
  private async sendAsset(assetId: string) {
    const buf = this.store.buffers.get(assetId);
    if (!buf || this.dc?.readyState !== "open") return;
    const b64 = bytesToB64(wavBytes(buf, 32)); // lossless 32-bit float
    const CHUNK = 16000, total = Math.ceil(b64.length / CHUNK);
    for (let seq = 0; seq < total; seq++) {
      await this.drain();
      this.send2({ t: "asset", assetId, seq, total, data: b64.slice(seq * CHUNK, (seq + 1) * CHUNK) });
    }
  }

  // ---- recording permission ---------------------------------------------
  private resetRecPermissions() {
    const changed = this.remoteGrant !== "none" || this.grantedToPeer;
    this.remoteGrant = "none";
    this.grantedToPeer = false;
    this.partnerLatency = null;
    this.pipelineDelay = null; this.pipelineJb = null;
    this.onPeerRecording?.(false);
    if (changed) this.onRecPermission?.();
  }
  get connected() { return this.dc?.readyState === "open"; }
  /** The partner's live audio stream (what they send us in the call). */
  remoteStream(): MediaStream | null { return (this.remoteAudio?.srcObject as MediaStream | null) ?? null; }
  /** Ask the partner for permission to record their mic this session. */
  requestRecording(): boolean {
    if (!this.connected) return false;
    this.remoteGrant = "pending";
    this.send2({ t: "rec-request" });
    this.onRecPermission?.();
    return true;
  }
  /** Allow or revoke the partner recording our mic (this session only). */
  setGrantToPeer(granted: boolean) {
    this.grantedToPeer = granted;
    this.send2({ t: "rec-grant", granted });
    if (granted) this.sendLatency(); // so their takes of us can be lined up
    if (!granted) this.onPeerRecording?.(false);
    this.onRecPermission?.();
  }
  /** Tell the partner we started/stopped recording their mic. */
  notifyRecordingPeer(on: boolean) { this.send2({ t: "rec-state", on }); }

  // ---- latency (for lining up recordings of the partner) -------------------
  private sendLatency() {
    const s = this.localLatency?.();
    if (typeof s === "number" && isFinite(s)) this.send2({ t: "lat", s });
  }
  /** Ask the partner for a fresh report of their audio latency. */
  requestLatency() { this.send2({ t: "lat-req" }); }
  // ---- full-quality partner takes -------------------------------------------
  /** Ask the partner's app to record its mic locally for this take. */
  startPartnerTake(takeId: string) { this.send2({ t: "ptake-start", takeId }); }
  stopPartnerTake(takeId: string) { this.send2({ t: "ptake-stop", takeId }); }

  /** Partner side: send our locally recorded take (24-bit WAV) to the recorder. */
  async sendPartnerTake(takeId: string, buf: AudioBuffer, startedAt: number, latency: number) {
    if (this.dc?.readyState !== "open") return;
    const b64 = bytesToB64(wavBytes(buf, 24));
    const CHUNK = 16000, total = Math.ceil(b64.length / CHUNK);
    this.send2({ t: "ptake-meta", takeId, total, at: startedAt, latency });
    for (let seq = 0; seq < total; seq++) {
      await this.drain();
      if (this.dc?.readyState !== "open") return;
      this.send2({ t: "ptake", takeId, seq, data: b64.slice(seq * CHUNK, (seq + 1) * CHUNK) });
    }
  }

  private async recvTakeChunk(msg: any) {
    const part = this.takeParts.get(msg.takeId);
    if (!part) return;
    part.chunks[msg.seq] = msg.data;
    const have = part.chunks.filter(Boolean).length;
    this.onPartnerTakeProgress?.(msg.takeId, have / part.total);
    if (have < part.total) return;
    this.takeParts.delete(msg.takeId);
    try {
      const bytes = b64ToBytes(part.chunks.join(""));
      const buf = await this.engine.ctx.decodeAudioData(bytes.buffer.slice(0) as ArrayBuffer);
      this.onPartnerTake?.(msg.takeId, buf, part.at, part.latency);
    } catch (e) { console.error("partner take decode", e); }
  }

  // ---- clock sync (NTP-style) --------------------------------------------
  private startClockSync() {
    if (this.pingTimer) return;
    const ping = () => this.send2({ t: "ping", s: this.engine.ctx.currentTime });
    ping();
    this.pingTimer = setInterval(ping, 1000);
  }
  private stopClockSync() {
    clearInterval(this.pingTimer); this.pingTimer = null;
    this.clockSamples = []; this.clockOffset = null;
  }
  private onPong(msg: { s: number; r: number }) {
    const now = this.engine.ctx.currentTime;
    const rtt = now - msg.s;
    if (!(rtt >= 0) || rtt > 2) return;
    this.clockSamples.push({ rtt, off: msg.r + rtt / 2 - now });
    if (this.clockSamples.length > 20) this.clockSamples.shift();
    // The fastest exchanges have the least queuing, so the most symmetric delay.
    const best = this.clockSamples.reduce((a, b) => (b.rtt < a.rtt ? b : a));
    this.clockOffset = best.off;
  }

  // ---- measuring the partner's audio path ---------------------------------
  private probeAt: ((at: number) => void) | null = null;

  /** Partner side: put a short known chirp into our outgoing audio at a known
   *  audio-clock time (mixed with the mic, so speech isn't interrupted). */
  private async sendProbe() {
    const pc = this.pc, mic = this.localStream;
    const sender = pc?.getSenders().find((x) => x.track?.kind === "audio");
    if (!pc || !mic || !sender) return;
    const ctx = this.engine.ctx;
    const dest = ctx.createMediaStreamDestination();
    const micSrc = ctx.createMediaStreamSource(mic);
    micSrc.connect(dest);
    const chirp = ctx.createBufferSource();
    chirp.buffer = makeChirp(ctx);
    chirp.connect(dest);
    const original = sender.track;
    try {
      await sender.replaceTrack(dest.stream.getAudioTracks()[0]);
      const at = ctx.currentTime + 0.35;
      chirp.start(at);
      this.send2({ t: "lat-probe-at", at });
      await new Promise((r) => setTimeout(r, 1200));
    } finally {
      try { await sender.replaceTrack(original); } catch {}
      try { micSrc.disconnect(); chirp.disconnect(); } catch {}
    }
  }

  /** Our side: ask the partner for a probe chirp and time its arrival in our
   *  audio graph. Returns the path delay (seconds) or null if it couldn't be found. */
  async measurePartnerPath(): Promise<number | null> {
    const stream = this.remoteStream();
    if (!this.connected || !stream || this.clockOffset == null) return null;
    const ctx = this.engine.ctx;
    await ensureProbeWorklet(ctx);
    const src = ctx.createMediaStreamSource(stream);
    const node = new AudioWorkletNode(ctx, "webcut-probe-capture");
    const silent = ctx.createGain(); silent.gain.value = 0;
    src.connect(node); node.connect(silent); silent.connect(ctx.destination);
    const blocks: { t: number; d: Float32Array }[] = [];
    node.port.onmessage = (e) => blocks.push(e.data);
    const statsA = await this.delayStats();
    let sentAt: number | null = null;
    const gotAt = new Promise<number>((res) => { this.probeAt = res; setTimeout(() => res(NaN), 3000); });
    this.send2({ t: "lat-probe" });
    sentAt = await gotAt;
    this.probeAt = null;
    await new Promise((r) => setTimeout(r, 1300)); // capture until well after the chirp arrives
    const statsB = await this.delayStats();
    try { src.disconnect(); node.disconnect(); silent.disconnect(); } catch {}
    if (!isFinite(sentAt) || !blocks.length) return null;
    // Stitch the captured audio and find the chirp by cross-correlation.
    const sr = ctx.sampleRate, t0 = blocks[0].t;
    const total = blocks.reduce((n, b) => n + b.d.length, 0);
    const x = new Float32Array(total);
    let o = 0; for (const b of blocks) { x.set(b.d, o); o += b.d.length; }
    const tpl = makeChirp(ctx).getChannelData(0);
    let best = -Infinity, bestI = -1, energy = 0;
    for (let j = 0; j < tpl.length; j++) energy += tpl[j] * tpl[j];
    for (let i = 0; i + tpl.length < x.length; i++) {
      let c = 0, e = 0;
      for (let j = 0; j < tpl.length; j += 2) { const v = x[i + j]; c += v * tpl[j]; e += v * v; }
      const score = e > 1e-9 ? c / Math.sqrt(e * energy) : 0; // normalized: louder speech doesn't win
      if (score > best) { best = score; bestI = i; }
    }
    if (best < 0.3) return null; // not found (dropout, heavy processing)
    const arrived = t0 + bestI / sr;            // our clock
    const sentLocal = sentAt - this.clockOffset; // their send time in our clock
    const delay = arrived - sentLocal;
    if (!(delay > 0 && delay < 2)) return null;
    this.pipelineDelay = delay;
    this.pipelineJb = jbBetween(statsA, statsB);
    return delay;
  }

  /** Raw WebRTC delay counters right now: round-trip time of the connection and
   *  the cumulative jitter-buffer totals of the partner's incoming audio. Take
   *  one at the start and one at the end of a recording and diff the totals to
   *  get the jitter buffer actually used during it. */
  async delayStats(): Promise<{ rtt: number | null; jbDelay: number; jbCount: number } | null> {
    const pc = this.pc;
    if (!pc) return null;
    try {
      const report = await pc.getStats();
      let rtt: number | null = null, jbDelay = 0, jbCount = 0;
      report.forEach((r: any) => {
        if (r.type === "candidate-pair" && (r.nominated || r.selected) && r.state === "succeeded" && typeof r.currentRoundTripTime === "number") rtt = r.currentRoundTripTime;
        if (r.type === "inbound-rtp" && r.kind === "audio") { jbDelay += r.jitterBufferDelay ?? 0; jbCount += r.jitterBufferEmittedCount ?? 0; }
      });
      return { rtt, jbDelay, jbCount };
    } catch { return null; }
  }

  private async onRecRequestMsg() {
    if (this.grantedToPeer) { this.send2({ t: "rec-grant", granted: true }); return; }
    const ok = (await this.onRecRequest?.()) ?? false;
    if (this.connected) this.setGrantToPeer(ok);
  }

  private onControl(msg: any) {
    switch (msg.t) {
      case "rec-request": return void this.onRecRequestMsg();
      case "rec-grant":
        this.remoteGrant = msg.granted ? "granted" : this.remoteGrant === "pending" ? "denied" : "none";
        return void this.onRecPermission?.();
      case "rec-state": return void this.onPeerRecording?.(!!msg.on && this.grantedToPeer);
      case "lat-req": if (this.grantedToPeer) this.sendLatency(); return;
      case "ping": return this.send2({ t: "pong", s: msg.s, r: this.engine.ctx.currentTime });
      case "pong": return this.onPong(msg);
      case "lat-probe": if (this.grantedToPeer) void this.sendProbe(); return;
      case "lat-probe-at": this.probeAt?.(msg.at); return;
      case "ptake-start": if (this.grantedToPeer) this.onPartnerTakeStart?.(msg.takeId); return;
      case "ptake-stop": this.onPartnerTakeStop?.(msg.takeId); return;
      case "ptake-meta": this.takeParts.set(msg.takeId, { total: msg.total, chunks: [], at: msg.at, latency: msg.latency }); return;
      case "ptake": return void this.recvTakeChunk(msg);
      case "lat": if (typeof msg.s === "number" && isFinite(msg.s) && msg.s >= 0 && msg.s < 2) this.partnerLatency = msg.s; return;
      case "transport": return this.applyTransport(msg);
      case "project": return this.applyProject(msg.snapshot);
      case "need": return void (msg.assetIds || []).forEach((id: string) => this.sendAsset(id));
      case "asset": return this.recvAssetChunk(msg);
    }
  }

  private applyTransport(msg: any) {
    this.applyingRemote = true;
    if (msg.tempo && msg.tempo !== this.store.project.tempo) { this.store.project.tempo = msg.tempo; this.store.emitChange("project"); }
    if (msg.playing && typeof msg.at === "number") {
      // Start exactly when the partner's timeline is at msg.pos (in our clock). Without
      // a clock sync yet, assume the message arrived instantly but keep their lead time.
      const at = this.clockOffset != null ? msg.at - this.clockOffset
        : this.engine.ctx.currentTime + Math.max(0, msg.at - (msg.sent ?? msg.at));
      this.engine.play(msg.pos, at);
    }
    else if (msg.playing && !this.engine.playing) this.engine.play(msg.pos);
    else if (!msg.playing && this.engine.playing) { this.engine.pause(); this.engine.seek(msg.pos); }
    else if (msg.playing) this.engine.seek(msg.pos);
    setTimeout(() => { this.applyingRemote = false; }, 50);
  }

  private applyProject(snapshot: any) {
    if (!snapshot) return;
    this.suppressBroadcast = true;
    // Keep this machine's own recording setup (arm, inputs, monitoring) — the
    // partner's never applies here, and their edits must not disarm our tracks.
    const mine = new Map(this.store.project.tracks.map((t) => [t.id, localFields(t)]));
    const next = normalizeProject(snapshot);
    for (const t of next.tracks) { const m = mine.get(t.id); if (m) Object.assign(t, m); }
    this.store.project = next;
    this.lastSharedSig = sharedSig(this.sharedSnapshot()); // in sync now: don't echo it back
    if (!this.store.track(this.store.selectedTrackId || "")) this.store.selectedTrackId = snapshot.tracks[0]?.id ?? null;
    this.store.emitChange("project");
    setTimeout(() => { this.suppressBroadcast = false; }, 60);
    // request any audio we don't have yet
    const missing = (snapshot.assets || []).filter((a: any) => !this.store.buffers.has(a.id)).map((a: any) => a.id);
    if (missing.length) this.send2({ t: "need", assetIds: missing });
  }

  private async recvAssetChunk(msg: any) {
    let part = this.assetParts.get(msg.assetId);
    if (!part) { part = { total: msg.total, chunks: [] }; this.assetParts.set(msg.assetId, part); }
    part.chunks[msg.seq] = msg.data;
    if (part.chunks.filter(Boolean).length < part.total) return;
    this.assetParts.delete(msg.assetId);
    try {
      const bytes = b64ToBytes(part.chunks.join(""));
      const ab = bytes.buffer.slice(0) as ArrayBuffer;
      const buf = await this.engine.ctx.decodeAudioData(ab);
      this.store.buffers.set(msg.assetId, buf);
      this.store.emitChange("assets");
    } catch (e) { console.error("asset decode", e); }
  }

  disconnect() {
    this.active = false;
    this.room = null;
    clearTimeout(this.projTimer);
    try { this.dc?.close(); } catch {}
    try { this.pc?.close(); } catch {}
    try { this.ws?.close(); } catch {}
    this.dc = null; this.pc = null; this.ws = null;
    this.localStream?.getTracks().forEach((t) => t.stop());
    this.localStream = null;
    if (this.remoteAudio) { try { this.remoteAudio.srcObject = null; this.remoteAudio.pause(); } catch {} this.remoteAudio = null; }
    this.peerPresent = false; this.makingOffer = false; this.ignoreOffer = false;
    this.pendingIce = []; this.assetParts.clear(); this.takeParts.clear();
    this.resetRecPermissions();
    this.stopClockSync();
    // The store/transport hooks stay installed but are gated by `active`.
    this.onStatus?.("offline");
  }
}

/** A 40 ms 1→4 kHz linear chirp (Hann-windowed): easy to find by correlation,
 *  survives the Opus codec, and is brief enough not to disturb a conversation. */
function makeChirp(ctx: BaseAudioContext): AudioBuffer {
  const sr = ctx.sampleRate, n = Math.round(sr * 0.04);
  const b = ctx.createBuffer(1, n, sr), d = b.getChannelData(0);
  for (let i = 0; i < n; i++) {
    const t = i / sr, f0 = 1000, k = (4000 - 1000) / 0.04;
    d[i] = 0.5 * Math.sin(2 * Math.PI * (f0 * t + 0.5 * k * t * t)) * (0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  return b;
}

/** Average jitter-buffer delay (seconds) between two stats snapshots. */
export function jbBetween(a: { jbDelay: number; jbCount: number } | null, b: { jbDelay: number; jbCount: number } | null): number | null {
  if (a && b && b.jbCount > a.jbCount) return (b.jbDelay - a.jbDelay) / (b.jbCount - a.jbCount);
  if (b && b.jbCount > 0) return b.jbDelay / b.jbCount;
  return null;
}

const probeWorklets = new WeakSet<BaseAudioContext>();
/** A tiny worklet that posts each block with the audio-clock time it was captured at. */
async function ensureProbeWorklet(ctx: AudioContext) {
  if (probeWorklets.has(ctx)) return;
  const code = `registerProcessor("webcut-probe-capture", class extends AudioWorkletProcessor {
    process(inputs) { const ch = inputs[0] && inputs[0][0]; if (ch) this.port.postMessage({ t: currentTime, d: ch.slice() }); return true; }
  });`;
  const url = URL.createObjectURL(new Blob([code], { type: "application/javascript" }));
  try { await ctx.audioWorklet.addModule(url); probeWorklets.add(ctx); } finally { URL.revokeObjectURL(url); }
}
